
import { computed, defineComponent, onBeforeMount, reactive, ref, watch } from 'vue';
import { MyButton } from './components';
import { toast } from '@tencent/web-vbui/dist/components/t-toast/toast';
import { spin } from '@tencent/web-vbui/src/components/t-spin/spin';
import { TCheckbox } from '@tencent/web-vbui';
import { i18n, reportStatistics } from '@tencent/vb-web-base';
import { accountCheck, RESULTCODE } from '@tencent/vb-web-base/dist/accountTips';
import { isFusionBankApp, to } from '@tencent/vb-web-base-core';
import { baseMixins } from '@common/mixins/baseMixins';
import { GenerateInviteCode, GetLoanConfigList } from '../../core/network/NodeAPI';
import { IInviteCodeM1Config } from '../../core/interface/IModel';
import ClipboardJS from 'clipboard';
import {
  CallErrorType,
  isCommonError,
  openSharePanel,
  ShareChannel,
  shareImage,
  shareLink,
} from '@tencent/vb-app-bridge';
import { ShareResponse } from '@tencent/vb-app-bridge/lib/src/type/share';
import Contract from '@tencent/vb-web-contract';
import html2canvas from 'html2canvas';
import { getUrlQuery } from '@common/utils/param';

export default defineComponent({
  components: {
    MyButton,
    TCheckbox,
  },
  setup() {
    const { getT, getLang } = i18n;
    const id = getUrlQuery('id');
    // i18n.setLang('en-GB');
    const $t = getT();
    const lang = getLang();
    const { setTitle, showModalError, isEng } = baseMixins();
    setTitle($t('贷款优惠活动'));
    const shareRef = ref(null as HTMLElement | null);
    const isAgree = ref(false);
    const isShowAgreeTips = ref(false);
    const isSign = ref(true);
    const inviteCode = ref('');
    const shareVisible = computed(() => isFusionBankApp && configInfo.isShowShareIcon === '1');
    // 预览协议
    let contractPreviewInstance: Contract;
    // 签约协议
    let contractSignInstance: Contract;

    // 活动页面配置
    const configInfo = reactive<IInviteCodeM1Config>({
      prefixCode: '',
      contractName: '',
      contractKey: '',
      isShowShareIcon: '',
      pageShareLink: '',
      pageShareTitle: '',
      pageShareSubtitle: '',
      'banner-m1': '',
      'activity-m1': '',
      'guide-m1': '',
      'remark-m1': '',
      sharePic: '',
      sharePic2: '',
      shareTitle: '',
      shareSubtitle: '',
      shareIcon: '',
      kfs: '',
      tc: '',
    });

    const formatParam = (data: string): string => {
      try {
        const arr = JSON.parse(data);
        return arr[0][lang];
      } catch (err) {
        console.error(err);
        return '';
      }
    };

    // 获取页面配置
    const getConfigInfo = async () => {
      const [data, err] = await to(GetLoanConfigList<{ content: IInviteCodeM1Config[] }>('mgm_act_page'));
      if (err) {
        showModalError(err.message, err.code);
        return;
      }
      if (data?.content?.length) {
        const config = data.content.find((item) => item?.id === id);
        if (config) {
          configInfo.prefixCode = config.prefixCode;
          configInfo.contractKey = config.contractKey;
          configInfo.isShowShareIcon = config.isShowShareIcon;
          configInfo.pageShareLink = config.pageShareLink;
          configInfo.shareIcon = config.shareIcon;
          configInfo.kfs = config.kfs;
          configInfo.tc = config.tc;
          configInfo.contractName = formatParam(config.contractName);
          configInfo.pageShareTitle = formatParam(config.pageShareTitle);
          configInfo.pageShareSubtitle = formatParam(config.pageShareSubtitle);
          configInfo['banner-m1'] = formatParam(config['banner-m1']);
          configInfo['activity-m1'] = formatParam(config['activity-m1']);
          configInfo['guide-m1'] = formatParam(config['guide-m1']);
          configInfo['remark-m1'] = formatParam(config['remark-m1']);
          // 处理分享图片跨域问题，将static域名换成www
          configInfo.sharePic = formatParam(config.sharePic).replace('static', 'www');
          configInfo.sharePic2 = formatParam(config.sharePic2).replace('static', 'www');
          configInfo.shareTitle = formatParam(config.shareTitle);
          configInfo.shareSubtitle = formatParam(config.shareSubtitle);

          contractPreviewInstance = new Contract({
            key: configInfo.contractKey,
            autoSign: false,
            containerType: 'dialog',
            needAgree: false,
            hideBtn: true,
            needCheckBox: false,
            needTitle: false,
            needLogin: false,
          });
          contractSignInstance = new Contract({
            key: configInfo.contractKey,
            autoSign: false,
            containerType: 'dialog',
            needAgree: false,
            hideBtn: true,
            needCheckBox: false,
            needTitle: false,
          });
        }
      }
    };

    // 检查协议是否已经签约，已签约情况自动调用接口生成邀请码
    const checkIsSign = async () => {
      const returnObj = await contractSignInstance.checkSign();
      if (returnObj.msg === 'hasContract') {
        isSign.value = true;
        const [data, err] = await to(GenerateInviteCode(configInfo.prefixCode));
        if (err) {
          showModalError(err.message, err.code);
        }
        inviteCode.value = data.invitation_code;
      } else {
        isSign.value = false;
      }
    };

    onBeforeMount(async () => {
      spin.show();
      await getConfigInfo();
      spin.hide();
      const res = await accountCheck({
        needAutoLogin: true,
        loginReload: true,
        needActivateModal: true,
        needOpenModal: true,
      });

      if (res.code === RESULTCODE.LOGINRELOAD) {
        return;
      }
      if (res.code !== RESULTCODE.SUCCESS) {
        // navigator.pop();
        // 检测不成功 组件会走其他异常流程比如拉起开户 激活弹层
        return;
      }
      await checkIsSign();
    });

    watch(isAgree, (val) => {
      if (val) {
        timer && clearTimeout(timer);
        isShowAgreeTips.value = false;
      }
    });

    const checkProto = async () => {
      await contractPreviewInstance.preview();
    };

    let timer: NodeJS.Timeout;
    // 立即邀请按钮点击
    const handleClick = async () => {
      reportStatistics({
        evtName: 'inviteBtnClick',
        desc: 'mgm活动立即邀请按钮点击',
      });
      if (!isSign.value && !isAgree.value) {
        isShowAgreeTips.value = true;
        timer && clearTimeout(timer);
        timer = setTimeout(() => {
          isShowAgreeTips.value = false;
        }, 5000);
        return;
      }
      const res = await accountCheck({
        needAutoLogin: true,
        loginReload: true,
        needActivateModal: true,
        needOpenModal: true,
      });

      if (res.code === RESULTCODE.LOGINRELOAD) {
        return;
      }
      if (res.code !== RESULTCODE.SUCCESS) {
        // navigator.pop();
        // 检测不成功 组件会走其他异常流程比如拉起开户 激活弹层
        return;
      }
      spin.show();
      const [, err1] = await to(contractSignInstance.sign());
      if (err1) {
        spin.hide();
        reportStatistics({
          evtName: 'contractSignError',
          desc: '签署mgm合同失败',
        });
        showModalError(err1.message, err1.code);
        return;
      }
      reportStatistics({
        evtName: 'contractSignSuccess',
        desc: '签署mgm合同成功',
      });
      const [data, err2] = await to(GenerateInviteCode(configInfo.prefixCode));
      if (err2) {
        spin.hide();
        showModalError(err2.message, err2.code);
      }
      inviteCode.value = data.invitation_code;
      spin.hide();
    };

    const copyCode = () => {
      const clipboard = new ClipboardJS('.invite-code-copy');
      reportStatistics({
        evtName: 'copyCode',
        desc: '复制邀请码',
      });
      clipboard.on('success', () => {
        toast.show({
          title: $t('复制成功'),
        });
        clipboard.destroy();
      });
      clipboard.on('error', (err) => {
        console.log(err);
        toast.show({
          title: $t('复制失败'),
        });
        clipboard.destroy();
      });
    };

    const handlePhoto = async () => {
      reportStatistics({
        evtName: 'photoShare',
        desc: 'mgm活动图片邀请按钮点击',
      });
      spin.show();
      const dom = shareRef.value as HTMLElement;
      html2canvas(dom, {
        logging: false,
        scale: window.devicePixelRatio,
        // width: dom.clientWidth, // dom 原始宽度
        // height: dom.clientHeight,
        scrollY: 0,
        scrollX: 0,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      }).then(async (canvas) => {
        spin.hide();
        const imageBase64 = canvas.toDataURL('image/png');
        console.log(imageBase64);
        try {
          const channel = await openSharePanel([
            ShareChannel.FACEBOOK,
            ShareChannel.WECHAT_MESSAGE,
            ShareChannel.WECHAT_MOMENT,
            ShareChannel.MESSENGER,
            ShareChannel.MORE,
          ]);
          if (channel === ShareChannel.UNKNOWN) {
            // 需要处理未知的渠道
            return;
          }
          await shareImage(channel, { imagePath: imageBase64 });
        } catch (err) {
          if (isCommonError(err)) {
            if (err.type === CallErrorType.OTHER_ERROR) {
              const { msg, result } = err.result as ShareResponse;
              if (result === '0') {
                reportStatistics({
                  evtName: 'photoShareSuccess',
                  desc: 'mgm活动图片邀请分享成功',
                });
              } else {
                reportStatistics({
                  evtName: 'photoShareError',
                  desc: 'mgm活动图片邀请分享失败',
                });
              }
              console.log('msg', msg);
              // const { result, msg } = err.result;
              // console.log(result);
              // 0    分享成功
              // -1    分享失败
              // -2    分享渠道对应app没安装
              // -3    用户主动取消
              // -4    相册权限未经用户授权    涉及图片操作时需要该权限
            }
          }
        }
      });
    };

    const handleLink = async () => {
      reportStatistics({
        evtName: 'linkShare',
        desc: 'mgm活动链接邀请按钮点击',
      });
      try {
        const channel = await openSharePanel([
          ShareChannel.FACEBOOK,
          ShareChannel.WECHAT_MESSAGE,
          ShareChannel.WECHAT_MOMENT,
          ShareChannel.MESSENGER,
          ShareChannel.MORE,
        ]);
        if (channel === ShareChannel.UNKNOWN) {
          // 需要处理未知的渠道
          return;
        }
        /* eslint-disable */
        const result = await shareLink(channel, {
          thumbPath: configInfo.shareIcon,
          title: configInfo.shareTitle,
          link: `${window.origin}/h5/walletLoan/activity/index.html?inviteCode=${inviteCode.value}&id=${id}#/inviteCodeM2`,
          content: $t(configInfo.shareSubtitle, { inviteCode: inviteCode.value }),
        });
        if (result.result === '0') {
          reportStatistics({
            evtName: 'linkShareSuccess',
            desc: 'mgm活动链接邀请分享成功',
          });
        }
      } catch (err) {
        if (isCommonError(err)) {
          if (err.type === CallErrorType.OTHER_ERROR) {
            const { msg, result } = err.result as ShareResponse;
            if (result !== '0') {
              reportStatistics({
                evtName: 'linkShareError',
                desc: 'mgm活动链接邀请分享失败',
              });
            }
            console.log(msg);
            // 0    分享成功
            // -1    分享失败
            // -2    分享渠道对应app没安装
            // -3    用户主动取消
            // -4    相册权限未经用户授权    涉及图片操作时需要该权限
          }
        }
      }
    };
    // 分享页面
    const sharePage = async () => {
      reportStatistics({
        evtName: 'pageShare',
        desc: 'mgm活动页面分享按钮点击',
      });

      try {
        if (isFusionBankApp) {
          // 富融APP内分享
          const channel = await openSharePanel([
            ShareChannel.FACEBOOK,
            ShareChannel.WECHAT_MESSAGE,
            ShareChannel.WECHAT_MOMENT,
            ShareChannel.MESSENGER,
            ShareChannel.MORE,
          ]);
          if (channel === ShareChannel.UNKNOWN) {
            // 需要处理未知的渠道
            return;
          }
          const result = await shareLink(channel, {
            link: configInfo.pageShareLink,
            title: configInfo.pageShareTitle,
            content: configInfo.pageShareSubtitle,
            thumbPath: '',
          });
          if (result.result === '0') {
            reportStatistics({
              evtName: 'pageShareSuccess',
              desc: 'mgm活动页面分享成功',
            });
          }
        }
      } catch (err) {
        if (isCommonError(err)) {
          if (err.type === CallErrorType.OTHER_ERROR) {
            const { msg, result } = err.result as ShareResponse;
            if (result !== '0') {
              reportStatistics({
                evtName: 'pageShareError',
                desc: 'mgm活动页面分享失败',
              });
            }
            console.log(msg);
          }
        }
      }
    };

    // 预览合同
    async function previewContract(key: string) {
      const contractObj = new Contract({
        key,
        autoSign: false,
        containerType: 'dialog',
        needAgree: false,
        needLogin: false,
        needCheckBox: false,
        needBackBtn: !isFusionBankApp,
        needTitle: !isFusionBankApp,
        hideBtn: true,
      });

      await contractObj.preview();
    }

    return {
      lang,
      isEng,
      shareRef,
      configInfo,
      isSign,
      isAgree,
      previewContract,
      isShowAgreeTips,
      inviteCode,
      shareVisible,
      checkProto,
      handleClick,
      copyCode,
      handlePhoto,
      handleLink,
      sharePage,
    };
  },
});
